import { useMemo, useState } from 'react';
import {
  ArrowPathIcon, ExclamationTriangleIcon,
  ScaleIcon, TruckIcon, UserIcon, ChartBarIcon,
} from '@heroicons/react/24/outline';
import {
  usePulseFilters, usePulseOverview, useEngineerPulse, useVanPulse,
} from '../hooks/useStockPulse';

// Stock Pulse — ordered against used.
//
// Three views over the same data because the same question gets asked three
// ways: across the fleet (what are we ordering that we do not use, and using
// that we never replace), per engineer (does this order match his job cards),
// and per van (what cannot be accounted for between two counts).

const fmtQty = (n) => {
  const v = Number(n) || 0;
  return Number.isInteger(v) ? String(v) : v.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
};
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB',
  { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—');
const isoDay = (d) => new Date(d).toISOString().slice(0, 10);
const daysAgo = (n) => isoDay(new Date(Date.now() - n * 864e5));

const TABS = [
  { key: 'overview', label: 'Overview', icon: ChartBarIcon },
  { key: 'engineer', label: 'By engineer', icon: UserIcon },
  { key: 'van', label: 'Van reconciliation', icon: TruckIcon },
];

function Stat({ label, value, sub, tone = 'plain' }) {
  const tones = {
    plain: 'text-gray-900',
    good: 'text-emerald-700',
    warn: 'text-amber-600',
    bad: 'text-rose-600',
  };
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-400">{label}</p>
      <p className={`mt-1 text-2xl font-bold tabular-nums ${tones[tone]}`}>{value}</p>
      {sub ? <p className="mt-0.5 text-xs text-gray-500">{sub}</p> : null}
    </div>
  );
}

function Panel({ title, hint, children, count }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="flex items-baseline justify-between border-b border-gray-100 px-4 py-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          {hint ? <p className="mt-0.5 text-xs text-gray-500">{hint}</p> : null}
        </div>
        {count !== undefined
          ? <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold tabular-nums text-gray-600">{count}</span>
          : null}
      </div>
      {children}
    </div>
  );
}

function ExceptionList({ items, metric, empty }) {
  if (!items?.length) return <p className="px-4 py-6 text-center text-sm text-gray-400">{empty}</p>;
  return (
    <ul className="divide-y divide-gray-100">
      {items.map((r) => (
        <li key={r.sku} className="flex items-center justify-between gap-3 px-4 py-2">
          <span className="min-w-0">
            <span className="block truncate text-sm text-gray-900">{r.name}</span>
            <span className="block text-xs text-gray-400">{r.sku}</span>
          </span>
          <span className="shrink-0 text-sm font-semibold tabular-nums text-gray-700">{metric(r)}</span>
        </li>
      ))}
    </ul>
  );
}

// ── Overview ────────────────────────────────────────────────────────────────
function Overview({ filters }) {
  const [from, setFrom] = useState(daysAgo(29));
  const [to, setTo] = useState(isoDay(new Date()));
  const [van, setVan] = useState('');
  const [q, setQ] = useState('');

  const { data, isLoading, isError, error, refetch, isFetching } =
    usePulseOverview({ from, to, van });

  const items = useMemo(() => {
    const t = q.trim().toLowerCase();
    const rows = data?.items || [];
    return t ? rows.filter((r) => `${r.name} ${r.sku}`.toLowerCase().includes(t)) : rows;
  }, [data, q]);

  const s = data?.summary;

  return (
    <>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="text-sm">
          <span className="mb-1 block text-xs font-medium text-gray-500">From</span>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-900" />
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-xs font-medium text-gray-500">To</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-900" />
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-xs font-medium text-gray-500">Van</span>
          <select value={van} onChange={(e) => setVan(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-900">
            <option value="">All vans</option>
            {(filters?.vans || []).map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </label>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search item or SKU"
          className="min-w-[200px] flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-900" />
        <button type="button" onClick={() => refetch()}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50">
          <ArrowPathIcon className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {isError ? (
        <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error?.response?.data?.error || 'Could not load the pulse.'}
        </p>
      ) : null}
      {isLoading ? <p className="text-sm text-gray-400">Loading…</p> : null}

      {s ? (
        <>
          <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-5">
            <Stat label="Consumed" value={fmtQty(s.consumed)}
              sub={`${fmtQty(s.used)} used + ${fmtQty(s.faulty)} faulty`} />
            <Stat label="Ordered" value={fmtQty(s.ordered)} sub="requested by engineers" />
            <Stat label="Picked" value={fmtQty(s.picked)}
              sub={s.short > 0 ? `${fmtQty(s.short)} short-picked` : 'nothing short-picked'}
              tone={s.short > 0 ? 'warn' : 'plain'} />
            <Stat label="Replacement rate"
              value={s.replacement_rate === null ? '—' : `${s.replacement_rate}%`}
              sub="picked ÷ consumed"
              tone={s.replacement_rate === null ? 'plain'
                : s.replacement_rate < 80 ? 'bad' : s.replacement_rate > 120 ? 'warn' : 'good'} />
            <Stat label="Items moved" value={s.items_moved} sub={`${fmtDate(s.from)} – ${fmtDate(s.to)}`} />
          </div>

          <div className="mb-5 grid gap-4 lg:grid-cols-2">
            <Panel title="Used, never ordered" count={data.exceptions.used_not_ordered.length}
              hint="Consumed in this window with nothing requested to replace it.">
              <ExceptionList items={data.exceptions.used_not_ordered}
                metric={(r) => `${fmtQty(r.consumed)} used`} empty="Nothing — everything used was reordered." />
            </Panel>
            <Panel title="Ordered, never used" count={data.exceptions.ordered_not_used.length}
              hint="Requested but not consumed — stock accumulating on vans.">
              <ExceptionList items={data.exceptions.ordered_not_used}
                metric={(r) => `${fmtQty(r.ordered)} ordered`} empty="Nothing ordered has gone unused." />
            </Panel>
            <Panel title="Short-picked" count={data.exceptions.short_picked.length}
              hint="The storeroom could not supply what was asked for.">
              <ExceptionList items={data.exceptions.short_picked}
                metric={(r) => `${fmtQty(r.short)} short`} empty="Every order was picked in full." />
            </Panel>
            <Panel title="High breakage" count={data.exceptions.high_faulty.length}
              hint="A fifth or more of what was consumed was logged faulty.">
              <ExceptionList items={data.exceptions.high_faulty}
                metric={(r) => `${fmtQty(r.faulty)} faulty`} empty="No item stands out for breakage." />
            </Panel>
          </div>

          <Panel title="Every item" count={items.length}
            hint="Variance compares what reached the van against what was consumed.">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-400">
                    <th className="px-4 py-2 font-medium">Item</th>
                    <th className="px-3 py-2 text-right font-medium">Used</th>
                    <th className="px-3 py-2 text-right font-medium">Faulty</th>
                    <th className="px-3 py-2 text-right font-medium">Ordered</th>
                    <th className="px-3 py-2 text-right font-medium">Picked</th>
                    <th className="px-3 py-2 text-right font-medium">Variance</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((r) => (
                    <tr key={r.sku} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                      <td className="px-4 py-2">
                        <span className="block text-gray-900">{r.name}</span>
                        <span className="block text-xs text-gray-400">
                          {r.sku}{r.is_consumable ? ' · consumable' : ''}
                          {r.vans?.length ? ` · ${r.vans.length} van${r.vans.length === 1 ? '' : 's'}` : ''}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-700">{fmtQty(r.used)}</td>
                      <td className={`px-3 py-2 text-right tabular-nums ${r.faulty > 0 ? 'text-amber-600' : 'text-gray-300'}`}>{fmtQty(r.faulty)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-700">{fmtQty(r.ordered)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-700">{fmtQty(r.picked)}</td>
                      <td className={`px-3 py-2 text-right font-semibold tabular-nums ${
                        r.variance < 0 ? 'text-rose-600' : r.variance > 0 ? 'text-emerald-700' : 'text-gray-400'}`}>
                        {r.variance > 0 ? '+' : ''}{fmtQty(r.variance)}
                      </td>
                    </tr>
                  ))}
                  {!items.length ? (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-400">
                      Nothing moved in this window.
                    </td></tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </Panel>
        </>
      ) : null}
    </>
  );
}

// ── By engineer ─────────────────────────────────────────────────────────────
const VERDICT = {
  matches: { label: 'Matches', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  over: { label: 'Over-ordered', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  under: { label: 'Under-ordered', cls: 'bg-sky-50 text-sky-700 border-sky-200' },
  'used, not ordered': { label: 'Used, not ordered', cls: 'bg-sky-50 text-sky-700 border-sky-200' },
  'ordered, not used': { label: 'Not on job cards', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
};

function ByEngineer({ filters }) {
  const [engineer, setEngineer] = useState('');
  const [orderId, setOrderId] = useState('');
  const [useDates, setUseDates] = useState(false);
  const [from, setFrom] = useState(daysAgo(29));
  const [to, setTo] = useState(isoDay(new Date()));

  const params = useDates ? { from, to } : { order: orderId };
  const { data, isLoading, isError, error } = useEngineerPulse(engineer, params);

  return (
    <>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="text-sm">
          <span className="mb-1 block text-xs font-medium text-gray-500">Engineer</span>
          <select value={engineer} onChange={(e) => { setEngineer(e.target.value); setOrderId(''); }}
            className="min-w-[200px] rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-900">
            <option value="">Choose an engineer…</option>
            {(filters?.engineers || []).map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </label>

        {data?.orders?.length ? (
          <label className="text-sm">
            <span className="mb-1 block text-xs font-medium text-gray-500">Compare order</span>
            <select value={orderId || data.order?.id || ''}
              onChange={(e) => { setOrderId(e.target.value); setUseDates(false); }}
              className="min-w-[240px] rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-900">
              {data.orders.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.reference} · {fmtDate(o.submitted_at)} · {o.status}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        <label className="flex items-center gap-2 pb-2 text-sm text-gray-700">
          <input type="checkbox" checked={useDates} onChange={(e) => setUseDates(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-brand focus:ring-brand" />
          Use my own dates
        </label>

        {useDates ? (
          <>
            <label className="text-sm">
              <span className="mb-1 block text-xs font-medium text-gray-500">From</span>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-900" />
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-xs font-medium text-gray-500">To</span>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-900" />
            </label>
          </>
        ) : null}
      </div>

      {!engineer ? (
        <p className="rounded-lg border border-dashed border-gray-300 px-4 py-10 text-center text-sm text-gray-400">
          Choose an engineer to compare an order against the job cards he filled in since his previous one.
        </p>
      ) : null}
      {isError ? (
        <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error?.response?.data?.error || 'Could not load this engineer.'}
        </p>
      ) : null}
      {isLoading && engineer ? <p className="text-sm text-gray-400">Loading…</p> : null}

      {data?.order ? (
        <>
          <div className="mb-4 rounded-lg border border-gray-200 bg-white px-4 py-3">
            <p className="text-sm text-gray-900">
              <strong>{data.order.reference}</strong> · {data.order.status} · van {data.order.van_registration || '—'} · {data.order.storeroom || '—'}
            </p>
            <p className="mt-0.5 text-xs text-gray-500">
              Compared against job cards {data.window.basis} — {fmtDate(data.window.from)} to {fmtDate(data.window.to)}
            </p>
          </div>

          <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Stat label="Reconciles" value={data.totals.reconciling} sub="ordered = consumed" tone="good" />
            <Stat label="Over-ordered" value={data.totals.over} sub="asked for more than used" tone="warn" />
            <Stat label="Under-ordered" value={data.totals.under} sub="used more than asked for" />
            <Stat label="Not on job cards" value={data.totals.unexplained}
              sub="ordered, no usage logged" tone={data.totals.unexplained > 0 ? 'bad' : 'plain'} />
          </div>

          <Panel title="Line by line" count={data.items.length}
            hint="Consumed counts job cards plus faulty, the same as the reorder maths.">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-400">
                    <th className="px-4 py-2 font-medium">Item</th>
                    <th className="px-3 py-2 text-right font-medium">Consumed</th>
                    <th className="px-3 py-2 text-right font-medium">Ordered</th>
                    <th className="px-3 py-2 text-right font-medium">Gap</th>
                    <th className="px-3 py-2 font-medium">Verdict</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((r) => {
                    const v = VERDICT[r.verdict] || { label: r.verdict, cls: 'bg-gray-50 text-gray-600 border-gray-200' };
                    return (
                      <tr key={r.sku} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                        <td className="px-4 py-2">
                          <span className="block text-gray-900">{r.name}</span>
                          <span className="block text-xs text-gray-400">{r.sku}</span>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-700">{fmtQty(r.consumed)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-700">{fmtQty(r.ordered)}</td>
                        <td className={`px-3 py-2 text-right font-semibold tabular-nums ${
                          r.gap > 0 ? 'text-amber-600' : r.gap < 0 ? 'text-sky-700' : 'text-gray-400'}`}>
                          {r.gap > 0 ? '+' : ''}{fmtQty(r.gap)}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`inline-block rounded-full border px-2 py-0.5 text-xs font-medium ${v.cls}`}>
                            {v.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Panel>
        </>
      ) : null}

      {data && !data.order && engineer ? (
        <p className="rounded-lg border border-dashed border-gray-300 px-4 py-10 text-center text-sm text-gray-400">
          This engineer has not placed a stock order yet.
        </p>
      ) : null}
    </>
  );
}

// ── Van reconciliation ──────────────────────────────────────────────────────
function VanReconciliation({ filters }) {
  const [van, setVan] = useState('');
  const [fromId, setFromId] = useState('');
  const [toId, setToId] = useState('');

  const { data, isLoading, isError, error } = useVanPulse(van, { from: fromId, to: toId });
  const t = data?.totals;

  return (
    <>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="text-sm">
          <span className="mb-1 block text-xs font-medium text-gray-500">Van</span>
          <select value={van} onChange={(e) => { setVan(e.target.value); setFromId(''); setToId(''); }}
            className="min-w-[180px] rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-900">
            <option value="">Choose a van…</option>
            {(filters?.vans || []).map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </label>
        {data?.counts?.length > 1 ? (
          <>
            <label className="text-sm">
              <span className="mb-1 block text-xs font-medium text-gray-500">Opening count</span>
              <select value={fromId || data.from_count?.id || ''} onChange={(e) => setFromId(e.target.value)}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-900">
                {data.counts.map((c) => <option key={c.id} value={c.id}>{fmtDate(c.count_date)}</option>)}
              </select>
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-xs font-medium text-gray-500">Closing count</span>
              <select value={toId || data.to_count?.id || ''} onChange={(e) => setToId(e.target.value)}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-900">
                {data.counts.map((c) => <option key={c.id} value={c.id}>{fmtDate(c.count_date)}</option>)}
              </select>
            </label>
          </>
        ) : null}
      </div>

      {!van ? (
        <p className="rounded-lg border border-dashed border-gray-300 px-4 py-10 text-center text-sm text-gray-400">
          Choose a van to compare two stock counts against what it received and used in between.
        </p>
      ) : null}
      {isError ? (
        <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error?.response?.data?.error || 'Could not reconcile this van.'}
        </p>
      ) : null}
      {isLoading && van ? <p className="text-sm text-gray-400">Loading…</p> : null}
      {data?.note ? (
        <p className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600">{data.note}</p>
      ) : null}

      {t ? (
        <>
          {data.receipts_seen === 0 ? (
            <p className="mb-4 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                No collected orders landed on this van in this window, so anything restocked by hand is
                invisible to the maths below. Read these figures as indicative until the van is
                restocked through ordering.
              </span>
            </p>
          ) : null}

          <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-5">
            <Stat label="Opening" value={fmtQty(t.opening)} sub={fmtDate(data.from_count.count_date)} />
            <Stat label="Received" value={fmtQty(t.received)} sub="collected orders" />
            <Stat label="Used" value={fmtQty(t.used)} sub={`${fmtQty(t.faulty)} of it faulty`} />
            <Stat label="Counted" value={fmtQty(t.counted)} sub={fmtDate(data.to_count.count_date)} />
            <Stat label="Unaccounted" value={`${t.leakage > 0 ? '+' : ''}${fmtQty(t.leakage)}`}
              sub={`${t.short_lines} short · ${t.surplus_lines} surplus`}
              tone={t.leakage > 0 ? 'bad' : t.leakage < 0 ? 'warn' : 'good'} />
          </div>

          <Panel title="Line by line" count={data.items.length}
            hint="Expected = opening + received − used − faulty. Unaccounted is what the count could not find.">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-400">
                    <th className="px-4 py-2 font-medium">Item</th>
                    <th className="px-3 py-2 text-right font-medium">Opening</th>
                    <th className="px-3 py-2 text-right font-medium">Received</th>
                    <th className="px-3 py-2 text-right font-medium">Used</th>
                    <th className="px-3 py-2 text-right font-medium">Expected</th>
                    <th className="px-3 py-2 text-right font-medium">Counted</th>
                    <th className="px-3 py-2 text-right font-medium">Unaccounted</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((r) => (
                    <tr key={r.sku} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                      <td className="px-4 py-2">
                        <span className="block text-gray-900">{r.name}</span>
                        <span className="block text-xs text-gray-400">{r.sku}</span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-600">{fmtQty(r.opening)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-600">{fmtQty(r.received)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-600">{fmtQty(r.used + r.faulty)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-900">{fmtQty(r.expected)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-900">{fmtQty(r.counted)}</td>
                      <td className={`px-3 py-2 text-right font-semibold tabular-nums ${
                        r.leakage > 0 ? 'text-rose-600' : r.leakage < 0 ? 'text-amber-600' : 'text-gray-300'}`}>
                        {r.leakage > 0 ? '+' : ''}{fmtQty(r.leakage)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        </>
      ) : null}
    </>
  );
}

function StockPulse() {
  const [tab, setTab] = useState('overview');
  const { data: filters } = usePulseFilters();

  return (
    <div className="p-6">
      <div className="mb-1 flex items-center gap-2">
        <ScaleIcon className="h-6 w-6 text-brand" />
        <h1 className="text-xl font-bold text-gray-900">Stock Pulse</h1>
      </div>
      <p className="mb-5 text-sm text-gray-500">
        What was ordered against what the job cards say was actually used.
      </p>

      <div className="mb-5 flex gap-1 border-b border-gray-200">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} type="button" onClick={() => setTab(key)}
            className={`-mb-px inline-flex items-center gap-1.5 border-b-2 px-4 py-2 text-sm font-medium ${
              tab === key
                ? 'border-brand text-brand'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'}`}>
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {tab === 'overview' ? <Overview filters={filters} /> : null}
      {tab === 'engineer' ? <ByEngineer filters={filters} /> : null}
      {tab === 'van' ? <VanReconciliation filters={filters} /> : null}
    </div>
  );
}

export default StockPulse;
